import React, { FC, useContext } from 'react';
import styles from './ProfileSettingsForm.module.scss';
import { Button, Input, Checkbox, TreeSelect, FileUploader } from 'esa-ui-library';
import {
	Form,
	emailValidator,
	usernameValidator,
	EMAIL_INVALID_MESSAGE,
	USERNAME_INVALID_MESSAGE,
	PERMISSIONS,
} from '../../../..';
import { PropertyType, useStore, useValidator } from '../../../../../common';
import { AuthContext } from '../../../../../logic';

interface ProfileSettingsFormProps {
	onSubmit: () => void;
}

const ProfileSettingsForm: FC<ProfileSettingsFormProps> = ({ onSubmit }): JSX.Element => {
	const { user, token, setUser } = useContext(AuthContext);
	const [state, dispatch] = useStore();
	const {
		userId,
		username,
		usernameInvalid,
		email,
		emailInvalid,
		partners,
		permissions,
		image,
		activePartners,
		errors,
		success,
	} = state;
	const canChangeUserDetails = user?.permissions?.isSuperAdmin || user?.permissions[PERMISSIONS.canChangeUserDetails];
	const canChangeGroupMemberships =
		user?.permissions?.isSuperAdmin || user?.permissions[PERMISSIONS.canChangeGroupMemberships];
	const isCurrentUser = user?.id === userId;
	const allPartnersSelected = permissions?.isSuperAdmin || activePartners.some((p: PropertyType) => p.value == '0');

	const isSubmitDisabled = [!!username, !usernameInvalid, !!email, !emailInvalid, activePartners.length > 0].some(
		(i) => !i
	);

	useValidator(email, emailValidator, 'EMAIL_INVALID');
	useValidator(username, usernameValidator, 'USERNAME_INVALID');

	const onUsernameChange = (val: string) => {
		dispatch('USERNAME', val);
	};

	const onEmailChange = (val: string) => {
		dispatch('EMAIL', val);
	};

	const onPartnersChange = (val: PropertyType[]) => {
		dispatch('PARTNERS_UPDATE', val);
	};

	const onAllPartnersChange = (val: boolean) => {
		const topLevel = { label: 'All partners', value: '0' };
		return onPartnersChange(val ? [topLevel] : []);
	};

	const onImageChange = async (val: File | null) => {
		await dispatch('IMAGE_UPDATE', { image: val, token });
		if (!isCurrentUser) return dispatch('USER_INIT', { token, id: userId });
		dispatch('GET_UPDATED_USER', { token, setUser });
	};

	return (
		<section className={styles.ProfileSettingsForm}>
			<Form errors={errors} success={success}>
				<FileUploader
					disabled={!canChangeUserDetails && !isCurrentUser}
					image={image}
					placeholder='JPG, PNG or GIF. Max size 100kb'
					dataId='image-field'
					onChange={onImageChange}
				/>
				<Input type='text' label='User ID' value={userId} dataId='user-id-field' disabled />
				<Input
					type='text'
					label='Username'
					value={username}
					dataId='username-field'
					disabled={!canChangeUserDetails && !isCurrentUser}
					onChange={onUsernameChange}
					error={usernameInvalid}
					errorMessage={USERNAME_INVALID_MESSAGE}
				/>
				<Input
					type='text'
					label='Email'
					value={email}
					dataId='email-field'
					disabled={!canChangeUserDetails}
					onChange={onEmailChange}
					error={emailInvalid}
					errorMessage={EMAIL_INVALID_MESSAGE}
				/>
				{canChangeGroupMemberships && !permissions?.canViewUserDetails && (
					<Checkbox
						label='Select all partners'
						checked={allPartnersSelected}
						disabled={permissions?.isSuperAdmin || !canChangeUserDetails}
						onChange={onAllPartnersChange}
						className={styles.AllPartners}
						dataId='select-all-partners'
					/>
				)}
				<TreeSelect
					required
					label='Partners'
					options={partners}
					placeholder='Select partners'
					selected={activePartners}
					disabled={allPartnersSelected}
					preview={!canChangeGroupMemberships || !canChangeUserDetails}
					dataId='partners-field'
					onChange={onPartnersChange}
				/>
				<Button title='Save Changes' primary dataId='submit-button' disabled={isSubmitDisabled} onClick={onSubmit} />
			</Form>
		</section>
	);
};

export { ProfileSettingsForm };
